import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getStatusInfo } from '../../utils/orderStatus';

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateRange, setDateRange] = useState('all');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [updatingStatus, setUpdatingStatus] = useState(false);

  const statusOptions = [
    { value: 'all', label: 'All Statuses' },
    { value: 'pending', label: 'Pending' },
    { value: 'claimed', label: 'Claimed' },
    { value: 'in_transit', label: 'In Transit' },
    { value: 'delivered', label: 'Delivered' },
    { value: 'cancelled', label: 'Cancelled' },
    { value: 'refunded', label: 'Refunded' },
    { value: 'damaged', label: 'Damaged' }
  ];

  useEffect(() => {
    fetchOrders();
  }, [statusFilter, dateRange]);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      // Use admin API to get all orders
      const res = await fetch('/api/admin/orders', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      let allOrders = await res.json();
      
      if (!Array.isArray(allOrders)) {
        if (allOrders.orders) allOrders = allOrders.orders;
        else allOrders = [];
      }

      // Apply date filter
      if (dateRange !== 'all') {
        const days = parseInt(dateRange);
        const cutoff = new Date();
        cutoff.setDate(cutoff.getDate() - days);
        allOrders = allOrders.filter(o => new Date(o.createdAt) >= cutoff);
      }

      // Apply status filter
      if (statusFilter !== 'all') {
        allOrders = allOrders.filter(o => o.status === statusFilter);
      }

      // Sort by date (newest first)
      allOrders.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

      setOrders(allOrders);
    } catch (error) {
      console.error('Failed to fetch orders:', error);
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  const updateOrderStatus = async (orderId, newStatus, notes = '') => {
    setUpdatingStatus(true);
    try {
      const res = await fetch(`/api/orders/${orderId}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ status: newStatus, notes })
      });
      if (res.ok) {
        alert(`Order status updated to ${newStatus.replace('_', ' ')}`);
        fetchOrders();
        setSelectedOrder(null);
      } else {
        const error = await res.json();
        alert(error.message || 'Update failed');
      }
    } catch (error) {
      alert('Error updating status');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const filteredOrders = orders.filter(order =>
    order._id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    order.buyer?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    order.buyer?.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    order.hubId?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    order.producer?.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getSellerName = (order) => {
    if (order.hubId) return order.hubId.name || 'Hub';
    if (order.producer) return order.producer.farmName || order.producer.name || 'Producer';
    return 'N/A';
  };

  const formatDate = (dateString) => new Date(dateString).toLocaleString();

  if (loading) return <div className="text-center py-8">Loading orders...</div>;

  return (
    <div className="max-w-7xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">Order Management</h1>
        <div className="flex gap-2">
          <button onClick={fetchOrders} className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700">
            Refresh
          </button>
          <Link to="/admin" className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700">
            Back to Admin
          </Link>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
            <input
              type="text"
              placeholder="Order ID, buyer, seller..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full border rounded px-3 py-2"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="w-full border rounded px-3 py-2">
              {statusOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Date Range</label>
            <select value={dateRange} onChange={(e) => setDateRange(e.target.value)} className="w-full border rounded px-3 py-2">
              <option value="all">All time</option>
              <option value="7">Last 7 days</option>
              <option value="30">Last 30 days</option>
              <option value="90">Last 90 days</option>
            </select>
          </div>
        </div>
      </div>

      {/* Orders Table */}
      {filteredOrders.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">No orders found.</div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Order #</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Buyer</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Seller</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Pickup</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredOrders.map(order => {
                const statusInfo = getStatusInfo(order.status);
                return (
                  <tr key={order._id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-mono text-sm">#{order._id.slice(-8).toUpperCase()}</td>
                    <td className="px-4 py-3 text-sm">{formatDate(order.createdAt)}</td>
                    <td className="px-4 py-3">
                      <div className="text-sm font-medium">{order.buyer?.name || 'Unknown'}</div>
                      <div className="text-xs text-gray-500">{order.buyer?.email}</div>
                    </td>
                    <td className="px-4 py-3 text-sm">{getSellerName(order)}</td>
                    <td className="px-4 py-3 text-sm font-medium">${order.totalAmount?.toFixed(2) || '0.00'}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs ${statusInfo.bg} ${statusInfo.color}`}>
                        {statusInfo.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm">
                     {order.assignedPickupId?.name || 
                      order.assignedPickup?.name || 
                      order.pickup?.name || '-'}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {order.assignedPickupId?.name || order.assignedPickup?.name || order.pickup?.name || '-'}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => setSelectedOrder(order)}
                        className="text-blue-600 hover:text-blue-800 text-sm"
                      >
                        Manage
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Order Management Modal */}
      {selectedOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Order #{selectedOrder._id.slice(-8).toUpperCase()}</h2>
              <button onClick={() => setSelectedOrder(null)} className="text-gray-500 hover:text-gray-700">✕</button>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
              <div>
                <p className="text-gray-500">Buyer</p>
                <p className="font-medium">{selectedOrder.buyer?.name}</p>
                <p className="text-xs text-gray-500">{selectedOrder.buyer?.email}</p>
              </div>
              <div>
                <p className="text-gray-500">Seller</p>
                <p className="font-medium">{getSellerName(selectedOrder)}</p>
              </div>
              <div>
                <p className="text-gray-500">Pickup</p>
                <p className="font-medium">
                  {selectedOrder.assignedPickupId?.name || 
                   selectedOrder.assignedPickup?.name || 
                   selectedOrder.pickup?.name ||
                  (typeof selectedOrder.assignedPickupId === 'string' ? 'Assigned (ID only)' : 'Not assigned')}
               </p>
              </div>
              <div>
                <p className="text-gray-500">Pickup</p>
                <p className="font-medium">{selectedOrder.assignedPickupId?.name || 'Not assigned'}</p>
              </div>
              <div>
                <p className="text-gray-500">Total</p>
                <p className="font-medium text-lg text-green-600">${selectedOrder.totalAmount?.toFixed(2)}</p>
              </div>
            </div>

            <div className="border-t pt-4 mb-4">
              <h3 className="font-semibold mb-2">Update Status</h3>
              <div className="flex gap-2 flex-wrap">
                {['pending', 'claimed', 'in_transit', 'delivered', 'cancelled', 'refunded', 'damaged'].map(status => {
                  const statusInfo = getStatusInfo(status);
                  return (
                    <button
                      key={status}
                      onClick={() => updateOrderStatus(selectedOrder._id, status)}
                      disabled={updatingStatus}
                      className={`px-3 py-1 rounded text-sm ${statusInfo.bg} ${statusInfo.color} hover:opacity-75 disabled:opacity-50`}
                    >
                      {statusInfo.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="border-t pt-4">
              <h3 className="font-semibold mb-2">Products</h3>
              <div className="space-y-2">
                {selectedOrder.products?.map((item, idx) => (
                  <div key={idx} className="flex justify-between text-sm border-b py-1">
                    <span>{item.name} x {item.quantity}</span>
                    <span>${(item.price * item.quantity).toFixed(2)}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex justify-end mt-6">
              <Link
                to={`/order-confirmation/${selectedOrder._id}`}
                className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
                target="_blank"
              >
                View Full Order Details
              </Link>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
